import type { OnInit } from '@angular/core';
import type { MapWidgetData } from '@web/widgets/map/type/map-widget.type';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import type { MapMarker } from '@angular/google-maps';
import { GoogleMap, GoogleMapsModule, MapInfoWindow } from '@angular/google-maps';
import { Actions, ofType } from '@ngrx/effects';
import { filter, map, mergeMap, takeUntil } from 'rxjs';

import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';

import { CredentialsFacade } from '@web/store/credentials/credentials.facade';
import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { mapWidgetActions } from '@web/store/page-structure/widgets/widgets.actions';
import { mapOptions } from '@web/widgets/map/model/map-options';
import { mapStyles } from '@web/widgets/map/model/map-styles';
import { getStyles } from './map-view.component.styles';

@Component({
    selector: 'web-map-view',
    templateUrl: './map-view.component.html',
    styleUrls: ['./map-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, GoogleMapsModule],
})
export class MapViewComponent extends WebBaseViewComponent<MapWidgetData> implements OnInit {
    @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
    @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;

    protected getStyles = getStyles;

    public credentialsFacade: CredentialsFacade = inject(CredentialsFacade);
    private columnsFacade: ColumnsFacade = inject(ColumnsFacade);
    private sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private actions$: Actions = inject(Actions);

    mapOptions: google.maps.MapOptions = mapOptions;

    autoComplete: google.maps.places.Autocomplete;
    prevOpen: MapInfoWindow;

    blurMap: boolean = false;

    ngOnInit() {
        super.ngOnInit();

        this.actions$
            .pipe(
                takeUntil(this.unsubscribe$),
                ofType(mapWidgetActions.editing)
                // filter(({ widgetId }) => widgetId === this.widget.id),
            )
            .subscribe((action) => {
                this.blurMap = action.editing;
                this.changeDetectorRef.detectChanges();
            });
    }

    openInfo(window: MapInfoWindow, marker: MapMarker) {
        if (this.prevOpen) {
            this.prevOpen.close();
        }

        window.open(marker);
        this.prevOpen = window;
    }

    getSectionId() {
        return this.columnsFacade.columnById$(this.widget.columnId).pipe(
            filter((column) => !!column),
            mergeMap((column) => this.sectionsFacade.sectionById$(column.parentId)),
            map((section) => section.id)
        );
    }

    mapInitialized(mapRef: google.maps.Map) {
        this.widget$.pipe(takeUntil(this.unsubscribe$)).subscribe((widget) => {
            if (!widget) {
                return;
            }

            const styles = mapStyles[widget.data.mapStyle] || [];
            mapRef.setOptions({ styles });
        });
    }

    mapMoved() {
        this.updateWidgetData({ center: this.map.getCenter().toJSON() });
    }
    changeZoom() {
        const zoom = this.map.getZoom();
        this.updateWidgetData({ zoom });
    }
}
